import { useState } from 'react';
import { useRetroBrightProjects, useCreateRetroBright, useStartSession, useStopSession, useUpdateRetroBright } from '../../hooks/useRetrobright';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useToast } from '../ui/Toast';
import { useTranslation } from '../../i18n';

interface RetroBrightSectionProps {
  itemId: string;
  canEdit: boolean;
}

export function RetroBrightSection({ itemId, canEdit }: RetroBrightSectionProps) {
  const { data, isLoading } = useRetroBrightProjects(itemId);
  const createMutation = useCreateRetroBright();
  const startMutation = useStartSession();
  const stopMutation = useStopSession();
  const updateMutation = useUpdateRetroBright();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newTarget, setNewTarget] = useState('24');

  const projects = data?.data ?? [];

  async function handleCreate() {
    if (!newName.trim()) return;
    try {
      await createMutation.mutateAsync({
        itemId,
        data: { name: newName.trim(), targetHours: parseFloat(newTarget) || 0 },
      });
      toast('Retrobright project created', 'success');
      setShowCreate(false);
      setNewName('');
      setNewTarget('24');
    } catch {
      toast('Failed to create project', 'error');
    }
  }

  async function handleStart(projectId: string) {
    try {
      await startMutation.mutateAsync({ projectId });
      toast('Session started', 'success');
    } catch {
      toast('Failed to start session', 'error');
    }
  }

  async function handleStop(projectId: string) {
    try {
      await stopMutation.mutateAsync(projectId);
      toast('Session stopped', 'success');
    } catch {
      toast('Failed to stop session', 'error');
    }
  }

  async function handleComplete(projectId: string) {
    try {
      await updateMutation.mutateAsync({ projectId, data: { status: 'completed' } });
      toast('Project completed', 'success');
    } catch {
      toast('Failed to update project', 'error');
    }
  }

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <h3 style={{ fontSize: '0.8125rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)' }}>
          {t('retro.title')}
        </h3>
        {canEdit && (
          <Button size="small" variant="secondary" onClick={() => setShowCreate(!showCreate)}>
            {showCreate ? t('retro.cancel') : t('retro.newProject')}
          </Button>
        )}
      </div>

      {showCreate && (
        <div style={{
          padding: '0.75rem',
          marginBottom: '0.75rem',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'end',
        }}>
          <div style={{ flex: 1 }}>
            <Input label={t('retro.projectName')} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Case whitening" />
          </div>
          <Input label={t('retro.targetHours')} type="number" value={newTarget} onChange={(e) => setNewTarget(e.target.value)} />
          <Button size="small" onClick={handleCreate}>{t('retro.create')}</Button>
        </div>
      )}

      {isLoading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{t('retro.loading')}</div>
      ) : projects.length === 0 ? (
        <div style={{
          padding: '1.25rem',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          color: 'var(--text-muted)',
          fontSize: '0.8125rem',
          fontFamily: 'var(--font-mono)',
          textAlign: 'center',
        }}>
          {t('retro.noProjects')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {projects.map((project: any) => {
            const hasActiveSession = project.sessions?.length > 0 && !project.sessions[project.sessions.length - 1].endTime;
            const progressPercent = project.targetHours > 0
              ? Math.min(100, Math.round((project.totalHours / project.targetHours) * 100))
              : 0;

            return (
              <div key={project.id} style={{
                padding: '0.875rem',
                background: 'var(--bg-secondary)',
                border: `1px solid ${hasActiveSession ? '#7B68EE' : 'var(--border)'}`,
                borderRadius: 'var(--radius)',
                boxShadow: hasActiveSession ? '0 0 8px rgba(123, 104, 238, 0.3)' : 'none',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.8125rem' }}>{project.name}</span>
                  <span style={{
                    fontSize: '0.625rem',
                    fontFamily: 'var(--font-mono)',
                    padding: '0.125rem 0.5rem',
                    border: '1px solid var(--border)',
                    borderRadius: '100px',
                    color: project.status === 'completed' ? '#33FF33' : project.status === 'in_progress' ? '#7B68EE' : 'var(--text-muted)',
                  }}>
                    {project.status}
                  </span>
                </div>

                {/* Timer display */}
                <div style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: hasActiveSession ? '#7B68EE' : 'var(--text-primary)',
                  marginBottom: '0.5rem',
                  letterSpacing: '0.05em',
                }}>
                  {project.totalHours.toFixed(1)}h
                  {project.targetHours > 0 && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
                      / {project.targetHours}h
                    </span>
                  )}
                </div>

                {/* Progress bar */}
                {project.targetHours > 0 && (
                  <div style={{
                    height: '4px',
                    background: 'var(--bg-tertiary)',
                    borderRadius: '2px',
                    marginBottom: '0.5rem',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${progressPercent}%`,
                      background: 'linear-gradient(90deg, #FFB800, #7B68EE)',
                      borderRadius: '2px',
                    }} />
                  </div>
                )}

                {canEdit && project.status !== 'completed' && (
                  <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.5rem' }}>
                    {hasActiveSession ? (
                      <Button size="small" variant="danger" onClick={() => handleStop(project.id)}>
                        {t('retro.stopTimer')}
                      </Button>
                    ) : (
                      <Button size="small" onClick={() => handleStart(project.id)}>
                        {t('retro.startTimer')}
                      </Button>
                    )}
                    <Button size="small" variant="secondary" onClick={() => handleComplete(project.id)}>
                      {t('retro.complete')}
                    </Button>
                  </div>
                )}

                {/* Sessions summary */}
                {project.sessions?.length > 0 && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.6875rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                    {project.sessions.length} {project.sessions.length !== 1 ? t('common.sessions') : t('common.session')} {t('common.recorded')}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
